import { createOtherDocument } from "./otherDocumentsService";
import {
  sanitizeFileName,
  uploadOtherDocument,
} from "../utils/otherDocumentsUtils";

export type BookingOtherDocumentRow = {
  id: number;
  booking_other_documents_file: string;
};

export type LeanCustomsStorageDocument = {
  bucket: string;
  path: string;
};

function normalizeStoragePath(path: string): string {
  return path.replace(/^\//, "");
}

function pushOtherDocument(
  documents: LeanCustomsStorageDocument[],
  path: string
): void {
  const normalized = normalizeStoragePath(path);
  if (!documents.some((d) => d.path === normalized)) {
    documents.push({ bucket: "otherdocuments", path: normalized });
  }
}

export function otherDocumentDisplayName(path: string): string {
  return normalizeStoragePath(path).split("/").pop() ?? path;
}

function storedFileMatchesUpload(
  storedPath: string,
  uploadFileName: string
): boolean {
  const storedBase = otherDocumentDisplayName(storedPath);
  const sanitizedUpload = sanitizeFileName(uploadFileName);
  if (storedBase === sanitizedUpload || storedBase === uploadFileName) {
    return true;
  }
  const timestampPrefix = /^\d+_(.+)$/;
  const match = storedBase.match(timestampPrefix);
  return match?.[1] === sanitizedUpload;
}

export async function prepareLeanCustomsAttachments(args: {
  bookingId: number;
  companyId: number;
  newFiles: File[];
  existingOtherDocuments: BookingOtherDocumentRow[];
  selectedOtherDocumentIds: number[];
}): Promise<{
  documents: LeanCustomsStorageDocument[];
  savedToBookingCount: number;
  errors: string[];
}> {
  const {
    bookingId,
    companyId,
    newFiles,
    existingOtherDocuments,
    selectedOtherDocumentIds,
  } = args;

  const documents: LeanCustomsStorageDocument[] = [];
  const errors: string[] = [];
  let savedToBookingCount = 0;

  const selectedDocs = existingOtherDocuments.filter((doc) =>
    selectedOtherDocumentIds.includes(doc.id)
  );
  for (const doc of selectedDocs) {
    if (!doc.booking_other_documents_file) continue;
    pushOtherDocument(documents, doc.booking_other_documents_file);
  }

  for (const file of newFiles) {
    const existing = existingOtherDocuments.find((doc) =>
      storedFileMatchesUpload(doc.booking_other_documents_file, file.name)
    );

    if (existing?.booking_other_documents_file) {
      pushOtherDocument(documents, existing.booking_other_documents_file);
      continue;
    }

    const uploadResult = await uploadOtherDocument(bookingId, file, companyId);
    if (!uploadResult || uploadResult.error || !uploadResult.path) {
      errors.push(`${file.name}: failed to save to booking documents`);
      continue;
    }

    const createResult = await createOtherDocument(bookingId, uploadResult.path);
    if (createResult.error) {
      errors.push(`${file.name}: saved to storage but booking record failed`);
      continue;
    }

    savedToBookingCount += 1;
    pushOtherDocument(documents, uploadResult.path);
  }

  return { documents, savedToBookingCount, errors };
}
